import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Save, Upload, ExternalLink } from "lucide-react";

export default function CompanyProfile() {
  const { company, refresh } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (company) {
      setForm({
        ...company,
        countries_served: (company.countries_served || []).join(", "),
        certifications: (company.certifications || []).join(", "),
      });
    }
  }, [company]);

  if (!form) return <div>{t("common.loading")}</div>;

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      countries_served: form.countries_served.split(",").map((s) => s.trim()).filter(Boolean),
      certifications: form.certifications.split(",").map((s) => s.trim()).filter(Boolean),
    };
    await api.patch("/company", payload);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
    refresh();
  };

  const uploadImage = (field) => async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/files/upload-public", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setForm((f) => ({ ...f, [field]: data.url }));
  };

  const publicUrl = `/c/${form.slug}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{t("nav.company_profile")}</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1">Public Profile</h1>
        </div>
        <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="view-public-profile-link" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50">
          View public page <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      <form onSubmit={save} className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="company-profile-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Inp label="Company name" testid="company-name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Inp label="Industry" testid="company-industry" value={form.industry || ""} onChange={(v) => setForm({ ...form, industry: v })} />
          <Inp label="Country" testid="company-country" value={form.country || ""} onChange={(v) => setForm({ ...form, country: v })} />
          <Inp label="Website" testid="company-website" value={form.website || ""} onChange={(v) => setForm({ ...form, website: v })} />
          <Inp label="Contact email" testid="company-contact-email" type="email" value={form.contact_email || ""} onChange={(v) => setForm({ ...form, contact_email: v })} />
          <Inp label="Contact phone" testid="company-contact-phone" value={form.contact_phone || ""} onChange={(v) => setForm({ ...form, contact_phone: v })} />
          <Inp label="Export experience" testid="company-experience" value={form.export_experience || ""} onChange={(v) => setForm({ ...form, export_experience: v })} />
          <Inp label="Address" testid="company-address" value={form.address || ""} onChange={(v) => setForm({ ...form, address: v })} />
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">Description</label>
          <textarea data-testid="company-description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm min-h-[100px]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Inp label="Countries served (comma)" testid="company-countries-served" value={form.countries_served} onChange={(v) => setForm({ ...form, countries_served: v })} />
          <Inp label="Certifications (comma)" testid="company-certifications" value={form.certifications} onChange={(v) => setForm({ ...form, certifications: v })} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <ImageField label="Logo" url={form.logo_url} onUpload={uploadImage("logo_url")} testid="company-logo" />
          <ImageField label="Cover image" url={form.cover_url} onUpload={uploadImage("cover_url")} testid="company-cover" />
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-slate-200">
          {saved ? <div className="text-sm text-emerald-600 font-medium">Saved ✓</div> : <div />}
          <button data-testid="save-profile-button" type="submit" className="inline-flex items-center gap-2 px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold">
            <Save className="w-4 h-4" /> {t("common.save")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Inp({ label, testid, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{label}</label>
      <input data-testid={testid} type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
    </div>
  );
}

function ImageField({ label, url, onUpload, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{label}</label>
      <div className="mt-1.5 flex items-center gap-3">
        {url ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt={label} className="w-20 h-20 object-cover rounded border border-slate-200" />
        ) : (
          <div className="w-20 h-20 border border-dashed border-slate-300 rounded flex items-center justify-center text-slate-400 text-xs">No image</div>
        )}
        <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-2 border border-dashed border-slate-300 rounded-md text-sm text-slate-600 hover:border-[#0052CC]">
          <Upload className="w-4 h-4" /> Upload
          <input data-testid={`${testid}-upload`} type="file" accept="image/*" className="hidden" onChange={onUpload} />
        </label>
      </div>
    </div>
  );
}
